/**
 * @file DocumentsListPage.tsx
 * @description Реестр документов магазина (phase09; phase19.31 — зона
 * «Клиенты и сделки», часть 3; замечание пользователя «Журнал и сам
 * документ нужно переделать»).
 *
 * Страница — раскладка: заголовок в верхней панели (`CabinetPageHeader`) с
 * подписью «N документов», строка фильтров (поиск по номеру или контрагенту ·
 * вид · статус;
 * на телефоне — «Фильтры · N»), таблица от 1024 / компактный список
 * (`ResponsiveList`), постраничный вывод. Фильтры и страница — в адресе
 * (`useDocumentsRegistry`), колонки — `buildDocumentColumns`.
 *
 * Главного действия нет: документ создаётся из сделки (вкладка «Документы»)
 * или из заказа («Выставить счёт») — там есть основание и покупатель.
 * Незаполненные реквизиты продавца (признак сервера `legalProfileIncomplete`)
 * — янтарной полосой над списком: чего не хватает словами
 * (`legalProfileGaps`), ссылка в настройки; в строке — пометка. Порядок
 * строк — как отдал сервер (по дате документа, новые сверху).
 */

import { FileText, RefreshCw, Search } from "lucide-react";
import { useMemo } from "react";
import { useParams } from "react-router";

import { useHasPermission } from "../../../../auth/hooks";
import { CrmPagination } from "../../../../components/crm/CrmPagination";
import { buildDocumentColumns } from "../../../../components/crm/documents/document-columns";
import { DocumentsFilterBar } from "../../../../components/crm/documents/DocumentsFilterBar";
import { documentsQueryFiltered } from "../../../../components/crm/documents/documents-query";
import { LegalProfileNotice } from "../../../../components/crm/documents/LegalProfileNotice";
import { useDocumentsRegistry } from "../../../../components/crm/documents/useDocumentsRegistry";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import type { PageAction } from "../../../../components/ui/page-actions";
import { ResponsiveList } from "../../../../components/ui/ResponsiveList";
import { pluralRu } from "../../../../lib/plural";

/** «12 документов»; пока список не пришёл — без подписи. */
function documentsSubtitle(total: number, loading: boolean): string | undefined {
  if (loading && total === 0) return undefined;
  return `${total} ${pluralRu(total, ["документ", "документа", "документов"])}`;
}

export function DocumentsListPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = `/shop/${slug}`;
  const canOpenOrder = useHasPermission("SHOP_VIEW_ORDERS");
  const canOpenDeal = useHasPermission("SHOP_USE_CRM");
  const registry = useDocumentsRegistry();
  const { query, setQuery } = registry;

  const columns = useMemo(() => buildDocumentColumns({ base, canOpenOrder, canOpenDeal }), [base, canOpenOrder, canOpenDeal]);
  // Пробелы в реквизитах по всем документам страницы — одной полосой.
  const incomplete = registry.items.filter((doc) => doc.legalProfileIncomplete);
  const gaps = incomplete.flatMap((doc) => doc.legalProfileGaps ?? []);
  const menu: PageAction[] = [
    { key: "refresh", label: "Обновить список", icon: RefreshCw, run: registry.refresh, loading: registry.loading },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Документы"
        subtitle={documentsSubtitle(registry.total, registry.loading)}
        menu={menu}
        menuLabel="Другие действия с документами"
      />

      <DocumentsFilterBar query={query} onChange={setQuery} />

      {incomplete.length > 0 ? <LegalProfileNotice base={base} gaps={gaps} /> : null}

      <ResponsiveList
        aria-label="Реестр документов"
        rows={registry.items}
        columns={columns}
        rowKey={(doc) => doc.id}
        rowHref={(doc) => `${base}/crm/documents/${doc.id}`}
        rowLabel={(doc) => `Документ ${doc.number || "черновик"}`}
        // 360: контрагент и основание переносятся, а не обрезаются (QA-5 D7).
        compactWrap
        rowSize="double"
        compactRowSize="tall"
        loading={registry.loading}
        error={registry.error}
        onRetry={registry.refresh}
        filtered={documentsQueryFiltered(query)}
        skeletonRows={10}
        empty={{
          icon: FileText,
          title: "Документов пока нет",
          description: "Счёт, УПД или коммерческое предложение создаются из карточки сделки или заказа",
        }}
        filteredEmpty={{
          icon: Search,
          title: "Нет документов по выбранным условиям",
          description: query.search ? "Ищем по номеру документа и контрагенту: имени клиента, названию или ИНН" : undefined,
          onReset: registry.resetFilters,
        }}
      />

      <CrmPagination
        label="Страницы реестра документов"
        page={query.page}
        pageSize={query.pageSize}
        total={registry.total}
        onPageChange={(page) => setQuery({ page })}
        onPageSizeChange={(pageSize) => setQuery({ pageSize })}
      />
    </div>
  );
}
